import * as fabric from 'fabric';

export type PixelGlowOwnProps = {
  color: string;
  blur: number;
  intensity: number;
};

const pixelGlowDefaults: PixelGlowOwnProps = {
  color: '#ffffff',
  blur: 12,
  intensity: 1,
};

/** Number of evenly-spaced samples used per ring. Higher = smoother
 *  falloff at higher cost. WebGL 1.0 requires loop bounds to be
 *  compile-time constants, so this is baked into both paths. */
const STEPS = 16;

/** Fragment shader for the WebGL backend. For each transparent pixel,
 *  sample STEPS points around a circle of radius `uBlur` and emit a
 *  glow color whose alpha decays linearly with distance from the
 *  nearest opaque neighbor. Opaque pixels pass through unchanged. */
const fragmentSource = `
  precision highp float;
  uniform sampler2D uTexture;
  uniform float uStepW;
  uniform float uStepH;
  uniform vec4 uGlowColor;
  uniform float uBlur;
  uniform float uIntensity;
  varying vec2 vTexCoord;
  void main() {
    vec4 cur = texture2D(uTexture, vTexCoord);
    if (cur.a > 0.0) {
      gl_FragColor = cur;
      return;
    }
    float coverage = 0.0;
    for (int i = 0; i < ${STEPS}; i++) {
      float angle = float(i) / float(${STEPS}) * 6.28318531;
      vec2 dir = vec2(cos(angle), sin(angle));
      float hit = 0.0;
      for (int r = 1; r <= 4; r++) {
        float t = float(r) / 4.0;
        vec2 offset = dir * vec2(uStepW, uStepH) * (uBlur * t);
        vec4 s = texture2D(uTexture, vTexCoord + offset);
        if (s.a > 0.0) {
          hit = max(hit, 1.0 - t);
        }
      }
      coverage += hit;
    }
    coverage = coverage / float(${STEPS});
    coverage = clamp(coverage * uIntensity, 0.0, 1.0);
    gl_FragColor = vec4(uGlowColor.rgb, uGlowColor.a * coverage);
  }
`;

function hexToRgba01(color: string): [number, number, number, number] {
  const hex = color.replace('#', '');
  if (hex.length === 6) {
    return [
      parseInt(hex.substring(0, 2), 16) / 255,
      parseInt(hex.substring(2, 4), 16) / 255,
      parseInt(hex.substring(4, 6), 16) / 255,
      1,
    ];
  }
  if (hex.length === 8) {
    return [
      parseInt(hex.substring(0, 2), 16) / 255,
      parseInt(hex.substring(2, 4), 16) / 255,
      parseInt(hex.substring(4, 6), 16) / 255,
      parseInt(hex.substring(6, 8), 16) / 255,
    ];
  }
  return [1, 1, 1, 1];
}

/** Pixel-aware outer-glow filter for Fabric `FabricImage` objects.
 *  Walks the image's alpha channel and paints a soft colored halo
 *  along the visible-pixel boundary. Pairs with `PixelStrokeFilter`
 *  (same architectural reasoning — paint-surface layers and PNG
 *  cutouts have transparent backgrounds, so Fabric's native `shadow`
 *  with offset 0 produces the wrong silhouette).
 *
 *  2D backend: blurs an alpha-tinted copy, knocks out the original
 *  silhouette, composites under the original.
 *  WebGL backend: per-pixel fragment shader samples STEPS×4 points
 *  with linear distance falloff. */
export class PixelGlowFilter extends fabric.filters.BaseFilter<'PixelGlow', PixelGlowOwnProps> {
  static type = 'PixelGlow' as const;
  static defaults = pixelGlowDefaults;
  static uniformLocations = ['uGlowColor', 'uBlur', 'uIntensity'];

  declare color: PixelGlowOwnProps['color'];
  declare blur: PixelGlowOwnProps['blur'];
  declare intensity: PixelGlowOwnProps['intensity'];

  isNeutralState(): boolean {
    return !this.blur || this.blur <= 0 || !this.intensity || this.intensity <= 0;
  }

  getFragmentSource(): string {
    return fragmentSource;
  }

  sendUniformData(
    gl: WebGLRenderingContext,
    uniformLocations: Record<string, WebGLUniformLocation | null>,
  ): void {
    const [r, g, b, a] = hexToRgba01(this.color);
    if (uniformLocations.uGlowColor) gl.uniform4f(uniformLocations.uGlowColor, r, g, b, a);
    if (uniformLocations.uBlur) gl.uniform1f(uniformLocations.uBlur, this.blur);
    if (uniformLocations.uIntensity) gl.uniform1f(uniformLocations.uIntensity, this.intensity);
  }

  applyTo2d({ imageData }: { imageData: ImageData }): void {
    const blur = this.blur;
    if (!blur || blur <= 0) return;
    const intensity = this.intensity;
    if (!intensity || intensity <= 0) return;
    const { width, height } = imageData;
    if (width === 0 || height === 0) return;

    const source = document.createElement('canvas');
    source.width = width;
    source.height = height;
    const sCtx = source.getContext('2d');
    if (!sCtx) return;
    sCtx.putImageData(imageData, 0, 0);

    // Blur a copy of the source via the GPU-accelerated CSS filter
    // pipeline — same trick the marquee morphology helper uses for
    // grow/shrink. Much cheaper than the per-pixel sampling we'd
    // need to match the WebGL backend's quality.
    const blurred = document.createElement('canvas');
    blurred.width = width;
    blurred.height = height;
    const bCtx = blurred.getContext('2d');
    if (!bCtx) return;
    bCtx.filter = `blur(${blur}px)`;
    bCtx.drawImage(source, 0, 0);
    bCtx.filter = 'none';

    // Recolor the blurred silhouette to the glow color while keeping
    // the alpha curve.
    bCtx.globalCompositeOperation = 'source-in';
    bCtx.fillStyle = this.color;
    bCtx.fillRect(0, 0, width, height);

    // Knock out the original silhouette so we're left only with the
    // halo region (otherwise the glow tints the layer's pixels too).
    bCtx.globalCompositeOperation = 'destination-out';
    bCtx.drawImage(source, 0, 0);

    // Boost the halo alpha by `intensity`. Values >1 make the glow
    // more saturated; <1 makes it subtler. Clamped at 1.0 (255).
    if (intensity !== 1) {
      const id = bCtx.getImageData(0, 0, width, height);
      const d = id.data;
      for (let i = 3; i < d.length; i += 4) {
        const a = d[i] * intensity;
        d[i] = a > 255 ? 255 : a;
      }
      bCtx.globalCompositeOperation = 'source-over';
      bCtx.putImageData(id, 0, 0);
    }

    // Final composite: halo behind, original on top.
    const out = document.createElement('canvas');
    out.width = width;
    out.height = height;
    const oCtx = out.getContext('2d');
    if (!oCtx) return;
    oCtx.drawImage(blurred, 0, 0);
    oCtx.drawImage(source, 0, 0);

    const final = oCtx.getImageData(0, 0, width, height);
    imageData.data.set(final.data);
  }

  toObject(): { type: 'PixelGlow'; color: string; blur: number; intensity: number } {
    return {
      type: PixelGlowFilter.type,
      color: this.color,
      blur: this.blur,
      intensity: this.intensity,
    };
  }
}

fabric.classRegistry.setClass(PixelGlowFilter);

export const PIXEL_GLOW_TYPE = PixelGlowFilter.type;
